import { AgGridReact } from "ag-grid-react";
import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "ag-grid-enterprise";
import EditableGridActions from "../../../UI/EditableGridActions/EditableGridActions";
import { AG_GRID_MODULES, AG_GRID_OPTIONS, LONG_TERM_PROMOTION_SUB_TAB_ID, VOLUME_FORECAST_TAB_ID, PROMOTION_PLANNING_TAB_ID, PROMO_MASTER_TAB_ID, TOAST_CONTAINER_ID, AG_GRID_DEFAULT_COL_DEF, SHORT_TERM_PROMOTION_SUB_TAB_ID, FORECAST_REVIEW_TAB_ID, ANNUAL_SUMMARY_TAB_ID, TRADE_SPEND_REPORT_TAB_ID } from "../../../../shared/constants";
import { usaNumberFormatter, getToastOptions, removeInsertedRowsById, createDescription } from "../../../../shared/functions";
import CellEditTrack from "../../../../models/CellEditTrack.model";
import { toast } from "react-toastify";
import { AxiosError, AxiosResponse } from "axios";
import useFetch from "../../../../hooks/useFetchMSAL";
import { useDispatch, useSelector } from "react-redux";
import { promoMasterTabActions } from "../../../../store/slices/promo-master-tab-slice";
import { PromoMasterSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/PromoMasterSlicer.model";
import { RootState } from "../../../../store";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import { IRichCellEditorParams } from "ag-grid-community";
import { createPortal } from "react-dom";
import Modal from "../../../UI/Modal/Modal";
import ChangeLogTable from "../../../UI/ChangeLog/ChangeLogTable";
import { TradeAndPromotionPlanningFetchAPI } from "../../../../models/APIResponses.model";

const LongTermPromotionGrid = (props: {
  gridHeight: string;
  getChangeTrackState: (changeTrackState: boolean) => any;
}) => {
  const [, fetchData] = useFetch([]);

  // States
  const [gridRowData, setGridRowData] = useState<any[]>([]);
  const [gridRowDataCopy, setGridRowDataCopy] = useState<any[]>([]);
  const [editedColIds, setEditedColIds] = useState<any[]>([]);
  const [gridCellEdits, setGridCellEdits] = useState<{
    [key: string]: any;
  }>({});
  const [newRowCount, setNewRowCount] = useState<number>(1);
  const [changeTrackState, setChangeTrackState] = useState(false);
  const [insertedRows, setInsertedRows] = useState<any>({});
  const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);
  const [enableDelButton, setEnableDelButton] = useState(false);
  const [showDuplicateRowButton, setShowDuplicateRowButton] = useState(false);
  const modalCloseBtnRef = useRef(null);
  const [changeLogData, setChangeLogData] = useState([]);
  const [changeLogDataFetched, setChangeLogDataFetched] = useState(false);

  // Redux variables
  const dispatchFn = useDispatch();
  const globalCustomer: { id: number; name: string; } = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.globalCustomer);
  const tabApiDataFetched: boolean = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.tabApiDataFetched)[LONG_TERM_PROMOTION_SUB_TAB_ID];
  const activeSubTabId: string = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.activeSubTabId);
  const dropdownSlicers: { skus: string[]; } = useSelector((state: RootState) => state.promoMasterTab.dropdownSlicers);
  const selectedSlicers: PromoMasterSelectedSlicerModel = useSelector((state: RootState) => state.promoMasterTab.selectedSlicers);

  // Variables
  const { getChangeTrackState } = { ...props };
  const agGridRef = useRef<AgGridReact>(null);
  const gridColDef: any = [
    {
      cellDataType: "text",
      headerName: "ID",
      width: 120,
      // headerCheckboxSelection: true,
      // checkboxSelection: true,
      // showDisabledCheckboxes: true,
      valueGetter: (params: any) => (params.data.id) ? `PMLT-${params.data.id}` : "",
      tooltipValueGetter: (params: any) => params.value
    },
    {
      field: "customer",
      wrapText: true,
      autoHeight: true,
      tooltipField: "customer",
      cellDataType: "text",
      headerName: "Customer",
      // minWidth: 250
    },
    {
      field: "sku",
      wrapText: true,
      autoHeight: true,
      tooltipField: "sku",
      cellDataType: "text",
      headerName: "Product",
      // minWidth: 250,
      editable: (params: any) => {
        if (params.data.id && (params.data.id).toString().startsWith("new_")) {
          return true
        }
        return false
      },
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: dropdownSlicers.skus,
        valueListMaxWidth: "350px",
        searchType: "match",
        allowTyping: true,
        filterList: true,
        highlightMatch: true
      } as IRichCellEditorParams,
    },
    {
      field: "Promotion Name",
      wrapText: true,
      autoHeight: true,
      tooltipField: "Promotion Name",
      cellDataType: "text",
      headerName: "Long Term Name",
      editable: true,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      field: "Promo Description",
      wrapText: true,
      autoHeight: true,
      tooltipField: "Promo Description",
      cellDataType: "text",
      headerName: "Long Term Description",
      editable: true,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "List Price/Case",
      field: "List Price/Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "List Price/Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted
    },
    {
      colId: "OI Rate/Case",
      field: "OI Rate/Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "OI Rate / Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "Growth Agreement - OI % Rate/Case",
      field: "Growth Agreement - OI % Rate/Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Growth Agreement - OI Rate % / Case",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueSetter: (params: any) => {
        if (params.colDef.field === "Growth Agreement - OI % Rate/Case") {
          const newValue: number | null = parseFloat(params.newValue);
          const columnId = params.column.colId;
          if (isNaN(newValue) || newValue < 0 || newValue > 100) {
            toast.error("Growth Agreement - OI % Rate/Case should be between 0 to 100", {
              ...getToastOptions("error"),
              containerId: TOAST_CONTAINER_ID
            });
            params.data[columnId] = params.oldValue;
          } else {
            params.data[columnId] = usaNumberFormatter(newValue, 2);
          }
        }
      },
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `${numStr}%` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "BB Direct Rate/Case",
      field: "BB Direct Rate/Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "BB Direct Rate ($) / Case",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "Growth Agreement - BB Direct % Rate/Case",
      field: "Growth Agreement - BB Direct % Rate/Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Growth Agreement - BB Direct % Rate/Case",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueSetter: (params: any) => {
        if (params.colDef.field === "Growth Agreement - BB Direct % Rate/Case") {
          const newValue: number | null = parseFloat(params.newValue);
          const columnId = params.column.colId;
          if (isNaN(newValue) || newValue < 0 || newValue > 100) {
            toast.error("Growth Agreement - BB Direct % Rate/Case should be between 0 to 100", {
              ...getToastOptions("error"),
              containerId: TOAST_CONTAINER_ID
            });
            params.data[columnId] = params.oldValue;
          } else {
            params.data[columnId] = usaNumberFormatter(newValue, 2);
          }
        }
      },
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `${numStr}%` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "Every Day Price $ / Case",
      field: "Every Day Price $ / Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Long Term Price/Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted
    },
    {
      colId: "Every Day Baseline(Volume) / Week",
      field: "Every Day Baseline(Volume) / Week",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Long Term Baseline(Volume) / Week",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => usaNumberFormatter(params.value, 0),
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
  ];

  const fetchLongTermPromotionData = useCallback(async () => {
    const fetchingDataToastID = toast.loading("Fetching Long Term Promotion data...", {
      containerId: TOAST_CONTAINER_ID,
      ...getToastOptions("loading"),
    });
    try {
      const fetchAPIResponse: AxiosResponse<any> = await fetchData(`/long-term-promotion`, {
        method: "POST",
        data: {
          customer_id: globalCustomer.id,
          skus: selectedSlicers.skus
        }
      });

      toast.dismiss({
        id: fetchingDataToastID,
        containerId: TOAST_CONTAINER_ID,
      });

      if (fetchAPIResponse.data.data) {
        setGridCellEdits({});
        setInsertedRows({});
        setGridRowData(fetchAPIResponse.data.data);
        setGridRowDataCopy(structuredClone(fetchAPIResponse.data.data));
        dispatchFn(promoMasterTabActions.setSlicers(fetchAPIResponse.data.slicers));
        dispatchFn(promoMasterTabActions.setDropdownSlicers(fetchAPIResponse.data.dropdown_slicers));
        dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
          [LONG_TERM_PROMOTION_SUB_TAB_ID]: true
        }));
        setChangeLogDataFetched(true);

      } else {
        toast.error("Error in fetching buy plan data", {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("error"),
        });
      }
    } catch (error: Error | AxiosError | any) {
      console.error(`Request Error: ${error.name}`);
      toast.dismiss({
        id: fetchingDataToastID,
        containerId: TOAST_CONTAINER_ID,
      });
      toast.error(error?.response?.data?.error || "Error in fetching Long Term Promotion data", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
    }
  }, [dispatchFn, fetchData, selectedSlicers, globalCustomer.id]);

  /**
     * Inserts a new row into the grid
     */
  const addRowToGrid = (): void => {
    const newRowId: string = `new_${newRowCount.toString()}`;
    agGridRef.current!.api!.applyTransaction({
      add: [{ id: newRowId, customer: globalCustomer.name }],
      addIndex: 0,
    });
    setInsertedRows((insertedRows: any) => {
      return {
        ...insertedRows,
        [newRowId]: {
          customer: globalCustomer.name
        }
      };
    });
    setChangeTrackState(true);
    setNewRowCount((prev) => prev + 1);
  };

  /**
     * Updates cell values by using part site source update API
     * @param {any} gridCellEdits Cell edits in the grid
     * @param {any} insertedRows Newly inserted rows
     * @param {"reCalculate" | "update"} mode Re-calculate or update
     */
  const updateCellValues = async (
    gridCellEdits: any,
    insertedRows: any,
    mode: "update" | "reCalculate",
    data:any={}
  ): Promise<any> => {
    console.log(data)
    try {
      const calculateProcessToastID = toast.loading(`${(mode === "update") ? "Updating" : "Re-calculating"} Long Term Promotion data...`, {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("loading"),
      });

      const apiReqResponse: AxiosResponse = await fetchData(`/long-term-promotion${(mode === "update") ? "" : "/re-calculate"}`, {
        method: (mode === "update") ? "PUT" : "POST",
        data: {
          customer_id: globalCustomer.id,
          skus: selectedSlicers.skus,
          updated_data: gridCellEdits,
          inserted_data: insertedRows,
          description: createDescription(LONG_TERM_PROMOTION_SUB_TAB_ID, "update", {...gridCellEdits, ...insertedRows}, gridRowData),
          tabname: LONG_TERM_PROMOTION_SUB_TAB_ID,
          ...data
        }
      }
      );

      toast.dismiss({
        id: calculateProcessToastID,
        containerId: TOAST_CONTAINER_ID,
      });

      if (apiReqResponse.data.success || apiReqResponse.data.data) {
        toast.success(`Long Term Promotion data ${(mode === "update") ? "updated" : "re-calculated"} successfully`, {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("success"),
        });

        if (mode === "update") {
          dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
            [VOLUME_FORECAST_TAB_ID]: false,
            [PROMOTION_PLANNING_TAB_ID]: false,
            [LONG_TERM_PROMOTION_SUB_TAB_ID]: false,
            [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false,
            [FORECAST_REVIEW_TAB_ID]: false,
            [ANNUAL_SUMMARY_TAB_ID]: false,
            [TRADE_SPEND_REPORT_TAB_ID]: false
          }));
        } else {
          setGridRowDataCopy(structuredClone(apiReqResponse.data.data));
        }
        setChangeLogDataFetched(true);
        return true;
        

      } else if (apiReqResponse.data.integrity_error || apiReqResponse.data.value_error) {
        toast.error(apiReqResponse.data.integrity_error || apiReqResponse.data.value_error, {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("error"),
        });
        setGridCellEdits({});
        setChangeTrackState(false);
        setGridRowDataCopy(structuredClone([...gridRowData]));
        return false;
      } else if (apiReqResponse.data.error) {
        toast.error(apiReqResponse.data.error, {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("error"),
        });
        return false;
      }
    } catch (error: any) {
      console.error(error);
      toast.error(`Error in ${(mode === "update") ? "updating" : "re-calculating"} the Long Term Promotion data`, {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
    }
  };

  /**
* Sets selected rows on selection checkbox change
*/
  const onSelectionChangedHandler = () => {
    const selectedRows = agGridRef.current!.api!.getSelectedRows();
    setSelectedRowData(selectedRows);
    const selectedRowIds: any[] = selectedRows.map((el) => el.id);
    setSelectedRowIds(selectedRowIds);
  };

  /**
 * Deletes selected rows
 * @param {any[]} selectedRowIds Selected Row IDs
 */
  const deleteSelectedRowData = async (selectedRowIds: any[], data1:any) => {
    let deleteToastID;
    try {
      // Delete selected newly inserted rows
      const oldDataRowIds: any[] = selectedRowIds.filter(rowId => !rowId.toString().startsWith("new_"));
      setInsertedRows((insertedRows: any) => removeInsertedRowsById(insertedRows, selectedRowIds));

      if (oldDataRowIds.length) {
        deleteToastID = toast.loading("Deleting selected rows...", {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("loading"),
        });

        const deleteReqResponse: AxiosResponse =
          await fetchData(
            `/long-term-promotion`,
            {
              method: 'DELETE',
              data: { 
                row_ids_to_delete: selectedRowIds,
                description: createDescription(LONG_TERM_PROMOTION_SUB_TAB_ID, "delete", selectedRowData),
                tabname: LONG_TERM_PROMOTION_SUB_TAB_ID,
                ...data1
              }
            }
          );

        toast.dismiss({
          id: deleteToastID,
          containerId: TOAST_CONTAINER_ID,
        });

        if (deleteReqResponse.data.success) {
          toast.success("Data updated successfully", {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions("success"),
          });

          dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
            [VOLUME_FORECAST_TAB_ID]: false,
            [PROMOTION_PLANNING_TAB_ID]: false,
            [LONG_TERM_PROMOTION_SUB_TAB_ID]: false,
            [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false,
            [FORECAST_REVIEW_TAB_ID]: false,
            [ANNUAL_SUMMARY_TAB_ID]: false,
            [TRADE_SPEND_REPORT_TAB_ID]: false
          }));

        } else {
          toast.error(deleteReqResponse.data.error, {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions("error"),
          });
        }
      } else {
        const selectedRows: any[] | undefined = agGridRef.current?.api.getSelectedRows();
        agGridRef.current?.api.applyTransaction({
          remove: selectedRows
        });
      }
    } catch (error: any) {
      console.error(error);
      toast.error("Failed to delete the selected rows", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
    }
  };

  /**
 * Deletes selected rows from the grid
 */
  const deleteSelectedRowsFromGrid = (e:MouseEventHandler<HTMLButtonElement>, data:any): void => {
    deleteSelectedRowData(selectedRowIds, data);
    setSelectedRowIds([]);
    setEnableDelButton(false);
  };

  /**
   * Duplicate selected rows
   */
  const duplicateSelectedRowsFromGrid = (): void => {
    if (selectedRowIds.length == 1) {

      selectedRowData.forEach((row) => {
        const newRowId: string = `new_${newRowCount.toString()}`;
        let { id,
          "Promotion Name": promotionName,
          "Promo Description": promotionDescription,
          "Every Day Price $ / Case": everyday_price,
          sku,
          "List Price/Case": list_price,
          ...obj } = row;

        agGridRef.current!.api!.applyTransaction({
          add: [{ id: newRowId, ...obj }],
          addIndex: 0,
        });
        // agGridRef.current?.api.applyTransaction({ update: [row] });
        setInsertedRows((insertedRows: any) => {
          let { id,
            "Promotion Name": promotionName,
            "Promo Description": promotionDescription,
            'Every Day Price $ / Case': everyday_price,
            sku,
            "List Price/Case": list_price,
            ...obj } = row;
          return {
            ...insertedRows,
            [newRowId]: obj
          };
        });
        setChangeTrackState(true);
        setNewRowCount((prev) => prev + 1);
      })
    } else {
      alert('Only single row can be duplicated');
    }

  }

  /**
     * Removes highlight of edited cells
     */
  const removeEditedCellHighlights = useCallback((): void => {
    editedColIds.forEach((colId) => {
      agGridRef.current!.api.getColumnDef(colId)!.cellStyle = undefined;
    });
  }, [editedColIds]);

  /**
     * Event handler for reset button click
     * @param {any} params Reset button click event data
     */
  const resetCellEditsHandler = useCallback((): void => {
    try {
      const resetConsent: boolean = window.confirm(
        "Do you want to reset the changes done to the grid?"
      );

      if (resetConsent) {
        setGridCellEdits({});
        setInsertedRows({});
        setGridRowDataCopy(structuredClone(gridRowData));
        removeEditedCellHighlights();
      }
    } catch (error: Error | any) {
      console.error(`Error: ${error}`);
    }
  }, [gridRowData, removeEditedCellHighlights]);


  /**
     * Event handler for when cell editing is stopped
     * @param {any} params Cell editing stop event parameters
     */
  const onCellEditingStoppedHandler = (params: any) => {
    try {
      var id: string = params.node.data.id;
      const colName = params.column.colId;

      if (id.toString().startsWith("new_")) {
        setInsertedRows((prev: any) => {
          let newData = prev;
          let newRow = newData[id] ? newData[id] : {};
          newRow[params.colDef.field] = params.newValue;
          newData[id] = newRow;
          return newData;
        });
      } else {
        if (params.oldValue !== params.newValue) {

          let currentRowNode = agGridRef.current!.api!.getRowNode(
            params.node.id
          );

          //   params.colDef.cellStyle = (p: any) =>{ 
          //     return { backgroundColor: '#8af38a' }
          //   };

          //   params.api.refreshCells({
          //     force: true,
          //     columns: [params.column.getId()],
          //     rowNodes: [params.node]
          // });


          if (editedColIds.indexOf(params.column.getId()) === -1) {
            setEditedColIds((prev) => [...prev, params.column.getId()]);
          }


          setGridCellEdits((prev) => {
            let newTrackEdits = prev;
            let cellEditTrack = newTrackEdits[id] ? newTrackEdits[id] : {};
            cellEditTrack[params.colDef.field] = params.newValue;
            newTrackEdits[id] = cellEditTrack;
            return newTrackEdits;
          });

          setChangeTrackState(true);
          agGridRef.current!.api!.applyTransaction({
            update: [currentRowNode?.data],
          });
        }
      }
    } catch (error: any) {
      console.error(`Error: ${error}`);
    }
  };


  const getListPrice = async (customer: string, sku: string) => {
    const fetchingDataToastID = toast.loading("Fetching List Price...", {
      containerId: TOAST_CONTAINER_ID,
      ...getToastOptions("loading"),
    });
    try {
      const fetchAPIResponse: AxiosResponse<any> = await fetchData(`/get-list-price`, {
        method: "GET",
        params: {
          customer: customer,
          sku: sku
        }
      });

      toast.dismiss({
        id: fetchingDataToastID,
        containerId: TOAST_CONTAINER_ID,
      });
      if (fetchAPIResponse) {
        if (fetchAPIResponse.data.list_price_mapping.length > 0) {
          return fetchAPIResponse.data.list_price_mapping[0]['List Price/Case'];
        } else {
          return 1;
        }
        // return fetchAPIResponse.data
      } else {
        toast.error("Error in fetching List Prices", {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("error"),
        });
        return {}
      }
    } catch (error: Error | AxiosError | any) {
      console.error(`Request Error: ${error.name}`);
      toast.dismiss({
        id: fetchingDataToastID,
        containerId: TOAST_CONTAINER_ID,
      });
      toast.error(error?.response?.data?.error || "Error in fetching Long Term Promotion data", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
    }
  }

  const onCellEditCommit = async (event: any) => {
    const { colDef, newValue, data } = event;

    const updatedData = { ...data };
    if (
      updatedData.sku !== undefined &&
      colDef.field === 'sku' &&
      newValue !== event.oldValue
    ) {
      updatedData['List Price/Case'] = await getListPrice(updatedData.customer, updatedData.sku);
    }

    agGridRef.current?.api.applyTransaction({ update: [updatedData] });
  };

  /**
     * Async function to fetch data from GET /change-log
     */
    const fetchChangeLogData = async (): Promise<void> => {
        const fetchingDataToastID = toast.loading('Fetching change log data...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<any>> = await fetchData("/get-change-log", {
                method: "GET",
                params: {
                    tabName: LONG_TERM_PROMOTION_SUB_TAB_ID
                    }
            });

            if (fetchAPIResponse.data.data) {
                setChangeLogData(fetchAPIResponse.data.data.result);
                setChangeLogDataFetched(false);

            } else {
                toast.error('Error in fetching Change Log data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching Change Log data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    };

  useEffect(() => {
    if (selectedRowIds.length > 0) {
      setEnableDelButton(true);
      setShowDuplicateRowButton(true);
    } else {
      setEnableDelButton(false);
      setShowDuplicateRowButton(false);
    }
  }, [selectedRowIds]);


  useEffect(() => {
    if (
      Object.keys(insertedRows).length === 0 &&
      Object.keys(gridCellEdits).length === 0
    ) {
      setChangeTrackState(false);
    }
  }, [insertedRows, gridCellEdits]);

  useEffect(() => {
    if (
      !tabApiDataFetched &&
      activeSubTabId === PROMO_MASTER_TAB_ID
    ) {
      fetchLongTermPromotionData();
    }
  }, [tabApiDataFetched, activeSubTabId, selectedSlicers, fetchLongTermPromotionData]);

  // Update change tracker state
  useEffect(() => {
    getChangeTrackState(changeTrackState);
  }, [changeTrackState, getChangeTrackState]);

  const rowSelection: any = useMemo(() => {
    return {
      mode: 'multiRow',
      checkboxes: true,
      headerCheckbox: false,
      enableClickSelection: true,
    };
  }, []);

  return (
    <>
      <div className="row mb-2">
        <div className="d-flex justify-content-end">

          <div className="d-inline">
                <button className="btn btn-warning btn-sm" data-bs-toggle="modal" 
                            data-bs-target={"#changeLogModalLongTermPromotionsId"} onClick={()=>{
                                if(changeLogDataFetched){
                                  fetchChangeLogData();
                                }
                            }}>
                            Change Log
                </button>
            </div>
          <EditableGridActions
            tabId={LONG_TERM_PROMOTION_SUB_TAB_ID}
            disableIf={{
              resetBtn: !changeTrackState,
              saveBtn: !changeTrackState,
              reCalculateBtn: !changeTrackState,
              deleteRowBtn: !enableDelButton,
              duplicateRowBtn: !showDuplicateRowButton
            }}
            onClick={{
              resetBtn: resetCellEditsHandler,
              saveBtn: (e:MouseEventHandler<HTMLButtonElement>,data:any) =>{return updateCellValues(gridCellEdits, insertedRows, "update", data)},
              reCalculateBtn: () => updateCellValues(gridCellEdits, insertedRows, "reCalculate"),
              addRowBtn: addRowToGrid,
              deleteRowBtn: (e:MouseEventHandler<HTMLButtonElement>,data:any) => deleteSelectedRowsFromGrid(e, data),
              duplicateRowBtn: duplicateSelectedRowsFromGrid
            }}
          />
        </div>
      </div>

      <div className="ag-theme-balham mt-2" style={{ height: props.gridHeight, maxHeight: props.gridHeight,
         minWidth: '100%', overflowX: 'auto', overflowY: 'auto' }}>
        <AgGridReact
          ref={agGridRef}
          rowData={gridRowDataCopy}
          columnDefs={gridColDef}
          rowSelection={rowSelection}
          singleClickEdit={true}
          gridOptions={{
            ...(AG_GRID_OPTIONS as any),
            rowHeight: 22,
            suppressMenuHide: true,
            suppressColumnVirtualisation: true,
            onSelectionChanged: onSelectionChangedHandler,
            autoSizeStrategy: {
              type: "fitCellContents",
              skipHeader: true,
              colIds: [
                "List Price/Case",
                "OI Rate/Case",
                "Growth Agreement - OI % Rate/Case",
                "BB Direct Rate/Case",
                "Growth Agreement - BB Direct % Rate/Case",
                "Every Day Price $ / Case",
                "Every Day Baseline(Volume) / Week"
              ]
            }
          }}
          defaultColDef={{
            ...AG_GRID_DEFAULT_COL_DEF
          }}
          modules={[
            ...AG_GRID_MODULES
          ]}
          getRowId={(params) => `${params.data.id}`}
          onCellEditingStopped={onCellEditingStoppedHandler}
          onCellValueChanged={onCellEditCommit}
        />
      </div>

      {
          createPortal(
              <Modal
                  id={"changeLogModalLongTermPromotionsId"}
                  ref={modalCloseBtnRef}
                  title={""}
                  onModalClose={() => { }}
                  width="xl"
              >
                  <ChangeLogTable changeLogData={changeLogData}/>
              </Modal>,
              document.getElementById("modal-root") as HTMLElement
          )
      }
    </>
  );
};

export default LongTermPromotionGrid;
